import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { setHeroContent, setHeroImage, setHeroTitle } from '../../redux/hero.slice';
import laptopMockup from '../../assets/laptop-mockup.png';
import { PAGE_METADATA } from '../../pageMetadata';
import PageMetadata from '../../shared/PageMetadata';
import RelatedPulseLinks from '../../shared/RelatedPulseLinks';
import './BadmintonClubPaymentPage.css';
import { paymentFaqStructuredData } from './paymentFaqSchema';

const CANONICAL_HREF = 'https://www.shuttleops.com/badminton-club-payment-software';

const paymentSteps = [
  {
    number: '01',
    title: 'Set the club rules',
    text: 'Choose session prices, member and non-member rates, capacity, and attendance rules for each session.',
  },
  {
    number: '02',
    title: 'Let players book',
    text: 'Players join through the app. Waiting lists and signup windows keep attendance clear before club night starts.',
  },
  {
    number: '03',
    title: 'Keep payment status visible',
    text: 'ShuttleOps records charges, successful payments, failures, offline payments, balances, and club payouts in one workflow.',
  },
];

const BadmintonClubPaymentPage = () => {
  const dispatch = useDispatch();
  const metadata = PAGE_METADATA['/badminton-club-payment-software'];

  useEffect(() => {
    dispatch(setHeroImage(false));
    dispatch(setHeroTitle('Collect badminton club payments without chasing'));
    dispatch(
      setHeroContent(
        'Connect session bookings, attendance, and payments. See who owes what before club night.'
      )
    );
  }, [dispatch]);

  useEffect(() => {
    const canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    canonical.setAttribute('href', CANONICAL_HREF);
    document.head.appendChild(canonical);

    return () => canonical.remove();
  }, []);

  return (
    <main className="payment-page">
      <PageMetadata metadata={metadata} />
      <script type="application/ld+json">{JSON.stringify(paymentFaqStructuredData)}</script>

      <section className="payment-proof" aria-labelledby="payment-proof-title">
        <div className="payment-proof__copy">
          <span className="payment-kicker">A real larger-club workflow</span>
          <h2 id="payment-proof-title">99 attendance charges. One clear club payout.</h2>
          <p>
            Across five summer sessions, one club recorded 99 attendance charges in ShuttleOps.
            The system collected payments, marked successes, flagged failures, and settled them
            through one club payout.
          </p>
          <p>
            Club helpers could see live balances without needing access to the club bank account.
            Players could check their own balance and payment history in the app.
          </p>
        </div>
        <div className="payment-proof__visual">
          <img src={laptopMockup} alt="ShuttleOps club management dashboard on a laptop" />
          <span>Bookings, attendance, payments, and balances stay connected.</span>
        </div>
      </section>

      <section className="payment-section" aria-labelledby="payment-process-title">
        <div className="payment-section__heading">
          <span className="payment-kicker">From booking to payout</span>
          <h2 id="payment-process-title">How badminton club payments work</h2>
          <p>
            Payment records start with the session. That keeps the player list, attendance, and
            amount due tied to the same club-night record.
          </p>
        </div>
        <ol className="payment-steps">
          {paymentSteps.map((step) => (
            <li key={step.number}>
              <span aria-hidden="true">{step.number}</span>
              <h3>{step.title}</h3>
              <p>{step.text}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="payment-section payment-section--white" aria-labelledby="payment-options-title">
        <div className="payment-section__heading">
          <span className="payment-kicker">Match the way your club charges</span>
          <h2 id="payment-options-title">Use session fees, memberships, or both</h2>
        </div>
        <div className="payment-options">
          <article>
            <h3>Pay as you go</h3>
            <p>
              Charge the right member or non-member price for each session. Attendance and payment
              status remain together for easier review.
            </p>
          </article>
          <article>
            <h3>Memberships and monthly payments</h3>
            <p>
              Pro supports memberships and monthly payments. Some plans can group weekly charges
              into one monthly transaction.
            </p>
          </article>
          <article>
            <h3>Offline payments</h3>
            <p>
              Record payments made outside Stripe, so the club ledger still shows what each player
              has paid and what remains due.
            </p>
          </article>
        </div>
      </section>

      <section className="payment-section payment-section--split" aria-labelledby="payment-visibility-title">
        <div>
          <span className="payment-kicker">Shared facts, controlled access</span>
          <h2 id="payment-visibility-title">Give each person the payment view they need</h2>
          <p>
            Owners combine administrator and finance rights. Finance users can review money data,
            while players see only their own balances and payment history.
          </p>
          <p>
            The same app also holds player records, session bookings, attendance, club messages,
            and game history. Your treasurer does not need a separate payment spreadsheet.
          </p>
        </div>
        <aside className="payment-fee-note" aria-labelledby="payment-fees-title">
          <span>Payment fee FAQ</span>
          <h3 id="payment-fees-title">What does a Direct Debit payment cost?</h3>
          <p>
            Stripe charges 1% with a 20p minimum. ShuttleOps charges the same amount separately.
            Pro and Extra group weekly session charges into one monthly transaction.
            The final cost depends on payment size and timing.
          </p>
        </aside>
      </section>

      <RelatedPulseLinks
        articles={[]}
        label="Plan your payment setup"
        links={[
          { title: 'How to collect badminton club payments', path: '/guides/collect-badminton-club-payments' },
          { title: 'See ShuttleOps plans and pricing', path: '/pricing' },
          { title: 'Estimate your club costs and time savings', path: '/calculator' },
          { title: 'Explore all club manager features', path: '/features/managers' },
          { title: 'See how the Digital Peg Board runs club nights', path: '/pegboard' },
        ]}
      />
    </main>
  );
};

export default BadmintonClubPaymentPage;
